import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Phone, Plus, Syringe, Stethoscope, Scissors, PawPrint, AlertCircle, Trash2, Pencil, X, Check, Wallet, Banknote, CreditCard, Clock, ChevronRight, ChevronDown, MessageSquareText, FileText, Send, FlaskConical, Weight, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { getCustomer, getOperations, deleteOperation, updateCustomer, updatePet, getCustomerBalance, addBalanceTransaction, updateBalanceTransaction, deleteBalanceTransaction, getPetNotes, getPetLabResults, getPetWeightHistory, sendManualSms } from '../services/api';
import Loader from '../components/Loader';
import BottomNav from '../components/BottomNav';
import PetAvatar from '../components/PetAvatar';
import PetIcon from '../components/PetIcon';
import QuickOperationModal from '../components/QuickOperationModal';
import QuickPetModal from '../components/QuickPetModal';
import CustomerSmsModal from '../components/CustomerSmsModal';
import PetNotesModal from '../components/PetNotesModal';
import PetLabModal from '../components/PetLabModal';
import PetWeightModal from '../components/PetWeightModal';
import { DOG_BREEDS, CAT_BREEDS } from '../data/breeds';
import { toast } from 'sonner';

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [customer, setCustomer] = useState(null);
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPet, setSelectedPet] = useState(location.state?.petId || null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [quickOpModal, setQuickOpModal] = useState({ open: false, pet: null });
  const [addPetOpen, setAddPetOpen] = useState(false);
  const [opTypeFilter, setOpTypeFilter] = useState('');

  // Edit states
  const [editingCustomer, setEditingCustomer] = useState(false);
  const [customerForm, setCustomerForm] = useState({ name: '', phone: '' });
  const [editingPetId, setEditingPetId] = useState(null);
  const [petForm, setPetForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Balance states
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [balanceData, setBalanceData] = useState({ balance: 0, transactions: [] });
  const [balanceLoading, setBalanceLoading] = useState(false);

  // SMS modal
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [balanceSmsSending, setBalanceSmsSending] = useState(false);
  const [newTx, setNewTx] = useState({ type: 'tahsilat', amount: '', description: '' });
  const [editingTxId, setEditingTxId] = useState(null);
  const [editTxForm, setEditTxForm] = useState({ type: '', amount: '', description: '' });
  const [deletingTxId, setDeletingTxId] = useState(null);

  // Pet Notes
  const [petNotes, setPetNotes] = useState({});
  const [petLabCounts, setPetLabCounts] = useState({});
  const [petWeightLast, setPetWeightLast] = useState({});
  const [notesModalPetId, setNotesModalPetId] = useState(null);
  const [labModalPetId, setLabModalPetId] = useState(null);
  const [weightModalPetId, setWeightModalPetId] = useState(null);
  const [expandedPets, setExpandedPets] = useState({});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    try {
      const customerRes = await getCustomer(id);
      setCustomer(customerRes.data);
      const petIds = customerRes.data.pets?.map(p => p.id) || [];
      if (petIds.length > 0) {
        const opsResults = await Promise.all(petIds.map(petId => getOperations({ pet_id: petId })));
        const allOps = opsResults.flatMap(res => res.data);
        setOperations(allOps.sort((a, b) => new Date(b.date) - new Date(a.date)));
        loadAllCounts(customerRes.data.pets);
      }
    } catch (error) {
      console.error('Error loading customer:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBalance = async () => {
    setBalanceLoading(true);
    try {
      const res = await getCustomerBalance(id);
      setBalanceData(res.data);
    } catch (e) { console.error(e); }
    finally { setBalanceLoading(false); }
  };

  const loadNotesForPet = async (petId) => {
    try {
      const res = await getPetNotes(petId);
      setPetNotes(prev => ({ ...prev, [petId]: res.data }));
    } catch (e) { console.error(e); }
  };

  const loadAllCounts = async (pets) => {
    const results = await Promise.all(
      pets.map(async (p) => {
        const [notes, labs, weights] = await Promise.all([
          getPetNotes(p.id).then(r => r.data).catch(() => []),
          getPetLabResults(p.id).then(r => r.data).catch(() => []),
          getPetWeightHistory(p.id).then(r => r.data).catch(() => []),
        ]);
        return { petId: p.id, notes, labCount: labs.length, lastWeight: weights[0] || null };
      })
    );
    const noteMap = {};
    const labMap = {};
    const weightMap = {};
    results.forEach(r => {
      noteMap[r.petId] = r.notes;
      labMap[r.petId] = r.labCount;
      weightMap[r.petId] = r.lastWeight;
    });
    setPetNotes(noteMap);
    setPetLabCounts(labMap);
    setPetWeightLast(weightMap);
  };

  const openBalanceModal = () => {
    setShowBalanceModal(true);
    loadBalance();
  };

  const formatPhoneForWhatsApp = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.startsWith('0') ? '90' + cleaned.slice(1) : cleaned.startsWith('90') ? cleaned : '90' + cleaned;
  };

  const handleBalanceSms = async () => {
    if (balanceSmsSending || !customer?.phone) return;
    setBalanceSmsSending(true);
    const amount = Math.abs(balanceData.balance);
    const formatted = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(amount) + ' TL';
    const savedUser = JSON.parse(localStorage.getItem('agrosvet_user') || '{}');
    const clinicName = savedUser?.clinic_name || 'Kliniğimiz';
    const message = `Sayın ${customer.name}, kliniğimize ${formatted} bakiyeniz bulunmaktadır. Ödeme yapmanızı rica eder, iyi günler dileriz. ${clinicName}`;
    try {
      const res = await sendManualSms({
        phone: customer.phone,
        message,
        customer_id: customer.id,
        customer_name: customer.name,
        sms_type: 'manual_balance',
      });
      if (res.data?.success) {
        toast.success('Bakiye hatırlatma SMS gönderildi');
      } else {
        toast.error('SMS gönderilemedi');
      }
    } catch {
      toast.error('SMS gönderilemedi');
    } finally {
      setBalanceSmsSending(false);
    }
  };

  const handleBalanceWhatsApp = () => {
    if (!customer?.phone) return;
    const amount = Math.abs(balanceData.balance);
    const formatted = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(amount) + ' TL';
    const savedUser = JSON.parse(localStorage.getItem('agrosvet_user') || '{}');
    const clinicName = savedUser?.clinic_name || 'Kliniğimiz';
    const message = `Sayın ${customer.name}, kliniğimize ${formatted} bakiyeniz bulunmaktadır. Ödeme yapmanızı rica eder, iyi günler dileriz. ${clinicName}`;
    const waPhone = formatPhoneForWhatsApp(customer.phone);
    window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleAddTx = async () => {
    const amount = parseFloat(newTx.amount);
    if (!amount || amount <= 0) { toast.error('Tutar girin'); return; }
    try {
      await addBalanceTransaction(id, { type: newTx.type, amount, description: newTx.description });
      setNewTx({ type: 'tahsilat', amount: '', description: '' });
      loadBalance();
      toast.success('İşlem eklendi');
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
  };

  const handleUpdateTx = async (txId) => {
    const amount = parseFloat(editTxForm.amount);
    if (!amount || amount <= 0) { toast.error('Tutar girin'); return; }
    try {
      await updateBalanceTransaction(id, txId, { type: editTxForm.type, amount, description: editTxForm.description });
      setEditingTxId(null);
      loadBalance();
      toast.success('İşlem güncellendi');
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
  };

  const handleDeleteTx = async (txId) => {
    try {
      await deleteBalanceTransaction(id, txId);
      setDeletingTxId(null);
      loadBalance();
      toast.success('İşlem silindi');
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
  };

  const handleDeleteOperation = async (opId) => {
    if (deletingId) return;
    setDeletingId(opId);
    setDeleteConfirm(null);
    try {
      await deleteOperation(opId);
      setOperations(prev => prev.filter(op => op.id !== opId));
      toast.success(`İşlem silindi`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silinemedi');
    } finally {
      setDeletingId(null);
    }
  };

  // Customer edit
  const startEditCustomer = () => {
    setCustomerForm({
      name: customer.name,
      phone: customer.phone,
      tc_kimlik_no: customer.tc_kimlik_no || '',
      birth_date: customer.birth_date || '',
      password: '',
    });
    setEditingCustomer(true);
  };
  const validatePhone = (phone) => /^0\d{10}$/.test(phone);

  const saveCustomer = async () => {
    if (!validatePhone(customerForm.phone)) {
      toast.error('Telefon numarası 0 ile başlamalı ve tam 11 haneli olmalıdır');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...customerForm };
      if (!payload.password) delete payload.password;
      await updateCustomer(id, payload);
      setCustomer(prev => ({ ...prev, name: payload.name, phone: payload.phone, tc_kimlik_no: payload.tc_kimlik_no, birth_date: payload.birth_date }));
      setEditingCustomer(false);
      toast.success(`Müşteri güncellendi`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Hata');
    } finally {
      setSaving(false);
    }
  };

  // Pet edit
  const startEditPet = (pet) => {
    setPetForm({
      name: pet.name, species: pet.species,
      breed: pet.breed || '', gender: pet.gender || '',
      color: pet.color || '',
      birth_date: pet.birth_date || '', weight_grams: pet.weight_grams || '',
      is_neutered: pet.is_neutered === true ? 'yes' : pet.is_neutered === false ? 'no' : '',
      is_aggressive: pet.is_aggressive === true ? 'yes' : pet.is_aggressive === false ? 'no' : '',
      microchip_no: pet.microchip_no || '',
      notes: pet.notes || '',
    });
    setEditingPetId(pet.id);
  };
  const savePet = async (petId) => {
    if ((petForm.species === 'dog' || petForm.species === 'cat') && !petForm.breed?.trim()) {
      toast.error('Irk seçimi zorunludur'); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...petForm,
        birth_date: petForm.birth_date || null,
        weight_grams: petForm.weight_grams ? parseInt(petForm.weight_grams) : null,
        is_neutered: petForm.is_neutered === 'yes' ? true : petForm.is_neutered === 'no' ? false : null,
        is_aggressive: petForm.is_aggressive === 'yes' ? true : petForm.is_aggressive === 'no' ? false : null,
        microchip_no: petForm.microchip_no?.trim() || null,
      };
      await updatePet(petId, payload);
      setCustomer(prev => ({
        ...prev,
        pets: prev.pets.map(p => p.id === petId ? { ...p, ...petForm } : p)
      }));
      setEditingPetId(null);
      toast.success('Hasta güncellendi');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Hata');
    } finally {
      setSaving(false);
    }
  };

  const getSpeciesName = (s) => ({ dog: `Köpek`, cat: 'Kedi', bird: `Kuş` }[s] || `Diğer`);
  const getGenderName = (g) => ({ male: 'Erkek', female: `Dişi` }[g] || null);
  const getOperationIcon = (type) => {
    switch (type) {
      case 'vaccine': return <Syringe size={16} className="text-[#34C759]" />;
      case 'treatment': return <Stethoscope size={16} className="text-[#6b6de3]" />;
      case 'service': return <Scissors size={16} className="text-[#FF9500]" />;
      default: return null;
    }
  };
  const getOperationBadgeClass = (type) => {
    switch (type) {
      case 'vaccine': return 'badge-vaccine';
      case 'treatment': return 'badge-treatment';
      case 'service': return 'badge-service';
      default: return '';
    }
  };
  const formatCurrency = (amount) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(amount);

  if (loading) return <Loader fullScreen />;
  if (!customer) return <div className="min-h-screen bg-[#F2F2F7] flex items-center justify-center"><p className="text-[#8E8E93]">{`Müşteri bulunamadı`}</p></div>;

  const filteredOperations = operations.filter(op => {
    if (selectedPet && op.pet_id !== selectedPet) return false;
    if (opTypeFilter && op.type !== opTypeFilter) return false;
    return true;
  });
  const opTypes = [...new Set(operations.map(op => op.type))].filter(Boolean);

  return (
    <div className="min-h-screen bg-[#F2F2F7] pb-24">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        {/* Top Row: Back + Name + Actions */}
        <div className="px-5 pt-5 pb-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4 min-w-0">
              <button onClick={() => navigate(-1)} className="p-2 -ml-2 mt-0.5 rounded-xl hover:bg-gray-100 transition-colors" data-testid="back-btn">
                <ArrowLeft size={22} className="text-gray-900" />
              </button>
              {editingCustomer ? (
                <div className="space-y-2 flex-1">
                  <Input value={customerForm.name} onChange={(e) => setCustomerForm(p => ({ ...p, name: e.target.value }))} className="h-10 bg-[#F2F2F7] border-0 rounded-xl font-bold" data-testid="edit-customer-name" />
                  <Input value={customerForm.phone} onChange={(e) => setCustomerForm(p => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 11) }))} inputMode="numeric" maxLength={11} placeholder="05XXXXXXXXX" className="h-10 bg-[#F2F2F7] border-0 rounded-xl text-sm" data-testid="edit-customer-phone" />
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={customerForm.tc_kimlik_no} onChange={(e) => setCustomerForm(p => ({ ...p, tc_kimlik_no: e.target.value.replace(/\D/g, '').slice(0, 11) }))} inputMode="numeric" maxLength={11} placeholder="TC Kimlik No" className="h-10 bg-[#F2F2F7] border-0 rounded-xl text-sm" data-testid="edit-customer-tc" />
                    <Input type="date" value={customerForm.birth_date} onChange={(e) => setCustomerForm(p => ({ ...p, birth_date: e.target.value }))} placeholder="Doğum Tarihi" className="h-10 bg-[#F2F2F7] border-0 rounded-xl text-sm" data-testid="edit-customer-birthdate" />
                  </div>
                  <Input type="password" value={customerForm.password} onChange={(e) => setCustomerForm(p => ({ ...p, password: e.target.value }))} placeholder="Yeni Şifre (değiştirmek için doldurun)" className="h-10 bg-[#F2F2F7] border-0 rounded-xl text-sm" data-testid="edit-customer-password" />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveCustomer} disabled={saving} className="bg-[#34C759] hover:bg-green-600 rounded-full h-9 px-4 text-xs" data-testid="save-customer-btn">
                      <Check size={14} className="mr-1" /> Kaydet
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingCustomer(false)} className="rounded-full h-9 px-4 text-xs">
                      <X size={14} className="mr-1" /> {`Vazgeç`}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">{customer.name}</h1>
                    <div className="flex items-center gap-1.5">
                      <button onClick={startEditCustomer} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" data-testid="edit-customer-btn">
                        <Pencil size={14} className="text-[#8E8E93]" />
                      </button>
                      <button onClick={() => setSmsModalOpen(true)} className="px-2 py-1 rounded-lg bg-[#6b6de3]/10 hover:bg-[#6b6de3]/20 transition-colors" data-testid="customer-sms-btn">
                        <span className="text-[11px] font-bold text-[#6b6de3]">SMS</span>
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 text-sm text-[#8E8E93] flex-wrap">
                    <a href={`tel:${customer.phone}`} className="text-[#6b6de3] hover:underline flex items-center gap-1">
                      <Phone size={13} /> {customer.phone}
                    </a>
                    {customer.tc_kimlik_no && <span data-testid="customer-tc-display" className="text-[#8E8E93]">TC: {customer.tc_kimlik_no}</span>}
                    {customer.birth_date && <span data-testid="customer-birthdate-display" className="text-[#8E8E93]">{`Doğum: ${new Date(customer.birth_date + 'T00:00:00').toLocaleDateString('tr-TR')}`}</span>}
                  </div>
                </div>
              )}
            </div>

            {/* Balance Card */}
            {!editingCustomer && (
              <button onClick={openBalanceModal}
                className={`shrink-0 ml-4 px-4 py-2.5 rounded-2xl flex items-center gap-3 transition-all active:scale-[0.97] border ${
                  customer.balance < 0 ? 'bg-red-50 border-red-100' : customer.balance > 0 ? 'bg-green-50 border-green-100' : 'bg-[#F2F2F7] border-gray-200'
                }`}
                data-testid="balance-btn">
                <Wallet size={18} className={customer.balance < 0 ? 'text-[#FF3B30]' : customer.balance > 0 ? 'text-[#34C759]' : 'text-[#8E8E93]'} />
                <div className="text-right">
                  <p className={`text-base font-bold leading-tight ${customer.balance < 0 ? 'text-[#FF3B30]' : customer.balance > 0 ? 'text-[#34C759]' : 'text-gray-900'}`}>
                    {customer.balance < 0 ? `-${formatCurrency(Math.abs(customer.balance))}` : customer.balance > 0 ? `+${formatCurrency(customer.balance)}` : '0 TL'}
                  </p>
                  <p className="text-[10px] text-[#8E8E93] leading-tight mt-0.5">
                    {customer.balance < 0 ? 'Borçlu' : customer.balance > 0 ? 'Alacaklı' : 'Bakiye'}
                  </p>
                </div>
              </button>
            )}
          </div>
        </div>

        {/* Pet Filter Tabs */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar px-5 pb-3">
          <button onClick={() => setSelectedPet(null)} className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${selectedPet === null ? 'bg-[#6b6de3] text-white' : 'bg-[#F2F2F7] text-gray-900 hover:bg-gray-200'}`}>
            {`Tümü`}
          </button>
          {customer.pets?.map((pet) => (
            <button key={pet.id} onClick={() => setSelectedPet(pet.id)} className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${selectedPet === pet.id ? 'bg-[#6b6de3] text-white' : 'bg-[#F2F2F7] text-gray-900 hover:bg-gray-200'}`} data-testid={`pet-filter-${pet.id}`}>
              <PetAvatar species={pet.species} gender={pet.gender} size={16} />
              {pet.name}
            </button>
          ))}
          <button onClick={() => setAddPetOpen(true)} className="flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium bg-[#F2F2F7] text-[#6b6de3] hover:bg-[#6b6de3]/10 flex items-center gap-1.5 transition-colors" data-testid="add-pet-btn">
            <Plus size={15} /> Hasta Ekle
          </button>
        </div>
      </header>

      <main className="p-4 space-y-4">
        {/* Pet Cards */}
        {customer.pets?.filter(p => !selectedPet || p.id === selectedPet).map((pet) => {
          const isEditing = editingPetId === pet.id;
          const upcomingReminders = operations
            .filter(op => op.pet_id === pet.id && (op.type === 'vaccine' || op.type === 'treatment') && op.next_due_date)
            .sort((a, b) => new Date(a.next_due_date) - new Date(b.next_due_date))
            .slice(0, 5);

          const petAge = pet.birth_date ? (() => {
              const bd = new Date(pet.birth_date + 'T00:00:00');
              const now = new Date();
              let years = now.getFullYear() - bd.getFullYear();
              let months = now.getMonth() - bd.getMonth();
              if (now.getDate() < bd.getDate()) months--;
              if (months < 0) { years--; months += 12; }
              if (years > 0 && months > 0) return `${years} y ${months} ay`;
              if (years > 0) return `${years} yaş`;
              if (months > 0) return `${months} ay`;
              const days = Math.floor((now - bd) / 86400000);
              return `${days} gün`;
            })() : null;
          const petOps = operations.filter(op => op.pet_id === pet.id);
          const lastVisitDate = petOps.length > 0 ? new Date(petOps[0].date).toLocaleDateString('tr-TR') : null;
          const weightDisplay = pet.weight_grams > 0 ? (pet.weight_grams >= 1000 ? `${(pet.weight_grams / 1000).toFixed(1)} kg` : `${pet.weight_grams} g`) : null;
          const isExpanded = expandedPets[pet.id];

          return (
            <div key={pet.id} className="ios-card animate-fade-in" data-testid={`pet-card-${pet.id}`}>

              {/* ── Header Row ── */}
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#6b6de3]/10 to-[#6b6de3]/5 flex items-center justify-center shrink-0">
                  <PetAvatar species={pet.species} gender={pet.gender} size={26} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <h3 className="font-bold text-gray-900 text-base leading-tight">{pet.name}</h3>
                    {!isEditing && (
                      <button onClick={() => startEditPet(pet)} className="p-0.5 rounded-full hover:bg-gray-100" data-testid={`edit-pet-${pet.id}`}>
                        <Pencil size={12} className="text-[#C7C7CC]" />
                      </button>
                    )}
                    {pet.is_aggressive === true && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-red-50 text-[10px] font-semibold text-red-500" data-testid={`pet-aggressive-${pet.id}`}>
                        <AlertCircle size={10} /> Saldırgan
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#8E8E93] leading-tight mt-0.5">
                    {pet.breed || getSpeciesName(pet.species)}{pet.breed ? ` · ${getSpeciesName(pet.species)}` : ''}{pet.gender ? ` · ${getGenderName(pet.gender)}` : ''}
                  </p>
                </div>
                {!isEditing && (
                  <Button size="sm" onClick={() => setQuickOpModal({ open: true, pet })} className="bg-[#6b6de3] hover:bg-[#5658c4] rounded-full h-8 px-3 text-xs shrink-0" data-testid={`add-operation-${pet.id}`}>
                    <Plus size={14} className="mr-1" /> İşlem
                  </Button>
                )}
              </div>

              {/* ── Quick Info Chips ── */}
              {!isEditing && (
                <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`pet-chips-${pet.id}`}>
                  {petAge && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[#F2F2F7] text-[11px] font-medium text-gray-700" data-testid={`pet-age-${pet.id}`}>
                      <Clock size={11} className="text-[#8E8E93]" /> {petAge}
                    </span>
                  )}
                  {weightDisplay && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-50 text-[11px] font-medium text-amber-700" data-testid={`pet-weight-${pet.id}`}>
                      <Weight size={11} /> {weightDisplay}
                    </span>
                  )}
                  {pet.is_neutered === true && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-green-50 text-[11px] font-medium text-green-700" data-testid={`pet-neutered-${pet.id}`}>
                      <Check size={11} /> Kısır
                    </span>
                  )}
                  {pet.is_neutered === false && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-orange-50 text-[11px] font-medium text-orange-600" data-testid={`pet-neutered-${pet.id}`}>
                      <Scissors size={11} /> Kısır Değil
                    </span>
                  )}
                  {pet.microchip_no && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-50 text-[11px] font-medium text-blue-600" data-testid={`pet-microchip-${pet.id}`}>
                      <FileText size={11} /> Çipli
                    </span>
                  )}
                  {lastVisitDate && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[#F2F2F7] text-[11px] font-medium text-gray-600" data-testid={`pet-lastvisit-${pet.id}`}>
                      <Stethoscope size={11} className="text-[#8E8E93]" /> {lastVisitDate}
                    </span>
                  )}
                </div>
              )}

              {/* ── Pet Edit Form ── */}
              {isEditing && (
                <div className="space-y-3 p-3 bg-[#F2F2F7] rounded-xl mt-3">
                  <div>
                    <Label className="text-xs text-[#8E8E93]">Hasta Adı *</Label>
                    <Input value={petForm.name} onChange={(e) => setPetForm(p => ({ ...p, name: e.target.value }))} className="h-10 bg-white border-0 rounded-lg text-sm mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#8E8E93]">Tür</Label>
                    <div className="flex gap-1.5 mt-1">
                      {[{ v: 'dog', l: 'Köpek' }, { v: 'cat', l: 'Kedi' }, { v: 'bird', l: 'Kuş' }, { v: 'other', l: 'Diğer' }].map(s => (
                        <button key={s.v} type="button" onClick={() => setPetForm(p => ({ ...p, species: s.v, breed: '' }))}
                          className={`flex-1 py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1 transition-colors ${
                            petForm.species === s.v ? 'bg-[#6b6de3] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                          }`}>
                          <PetIcon species={s.v} size={13} /> {s.l}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-[#8E8E93]">{(petForm.species === 'dog' || petForm.species === 'cat') ? 'Irk *' : 'Irk'}</Label>
                      {(petForm.species === 'dog' || petForm.species === 'cat') ? (
                        <BreedSelect breeds={petForm.species === 'dog' ? DOG_BREEDS : CAT_BREEDS} value={petForm.breed} onChange={v => setPetForm(p => ({ ...p, breed: v }))} testId="edit-pet-breed-select" />
                      ) : (
                        <Input value={petForm.breed} onChange={(e) => setPetForm(p => ({ ...p, breed: e.target.value }))} className="h-10 bg-white border-0 rounded-lg text-sm mt-1" placeholder="Opsiyonel" />
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-[#8E8E93]">Cinsiyet</Label>
                      <div className="flex gap-1.5 mt-1">
                        {[{ v: 'male', l: 'Erkek' }, { v: 'female', l: 'Dişi' }].map(g => (
                          <button key={g.v} type="button" onClick={() => setPetForm(p => ({ ...p, gender: p.gender === g.v ? '' : g.v }))}
                            className={`flex-1 h-10 rounded-lg text-xs font-medium transition-colors ${petForm.gender === g.v ? 'bg-[#6b6de3] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>{g.l}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs text-[#8E8E93]">Renk</Label>
                      <Input value={petForm.color} onChange={(e) => setPetForm(p => ({ ...p, color: e.target.value }))} className="h-10 bg-white border-0 rounded-lg text-sm mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-[#8E8E93]">Doğum Tarihi</Label>
                      <Input type="date" value={petForm.birth_date || ''} onChange={(e) => setPetForm(p => ({ ...p, birth_date: e.target.value }))} className="h-10 bg-white border-0 rounded-lg text-sm mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-[#8E8E93]">Ağırlık (g)</Label>
                      <Input type="number" placeholder="3500" value={petForm.weight_grams || ''} onChange={(e) => setPetForm(p => ({ ...p, weight_grams: e.target.value }))} className="h-10 bg-white border-0 rounded-lg text-sm mt-1" />
                      {petForm.weight_grams && parseInt(petForm.weight_grams) >= 1000 && (
                        <p className="text-[10px] text-[#8E8E93]">{(parseInt(petForm.weight_grams) / 1000).toFixed(1)} kg</p>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs text-[#8E8E93]">Kısırlaştırılmış mı?</Label>
                      <div className="flex gap-1.5 mt-1">
                        {[{ v: 'yes', l: 'Evet' }, { v: 'no', l: 'Hayır' }].map(o => (
                          <button key={o.v} type="button" onClick={() => setPetForm(p => ({ ...p, is_neutered: p.is_neutered === o.v ? '' : o.v }))}
                            className={`flex-1 h-10 rounded-lg text-xs font-medium transition-colors ${petForm.is_neutered === o.v ? 'bg-[#6b6de3] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>{o.l}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-[#8E8E93]">Saldırgan mı?</Label>
                      <div className="flex gap-1.5 mt-1">
                        {[{ v: 'yes', l: 'Evet' }, { v: 'no', l: 'Hayır' }].map(o => (
                          <button key={o.v} type="button" onClick={() => setPetForm(p => ({ ...p, is_aggressive: p.is_aggressive === o.v ? '' : o.v }))}
                            className={`flex-1 h-10 rounded-lg text-xs font-medium transition-colors ${petForm.is_aggressive === o.v ? (o.v === 'yes' ? 'bg-[#FF3B30] text-white' : 'bg-[#34C759] text-white') : 'bg-white text-gray-700 hover:bg-gray-50'}`}>{o.l}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-[#8E8E93]">Mikroçip No</Label>
                      <Input value={petForm.microchip_no} onChange={(e) => setPetForm(p => ({ ...p, microchip_no: e.target.value }))} placeholder="Opsiyonel" className="h-10 bg-white border-0 rounded-lg text-sm mt-1" data-testid="edit-pet-microchip" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-[#8E8E93]">Notlar</Label>
                    <Input value={petForm.notes} onChange={(e) => setPetForm(p => ({ ...p, notes: e.target.value }))} placeholder="Kronik hastalıklar, engel durumu vb." className="h-10 bg-white border-0 rounded-lg text-sm mt-1" />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" onClick={() => savePet(pet.id)} disabled={saving} className="bg-[#34C759] hover:bg-green-600 rounded-full h-9 px-4 text-xs flex-1" data-testid={`save-pet-${pet.id}`}>
                      <Check size={14} className="mr-1" /> Kaydet
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingPetId(null)} className="rounded-full h-9 px-4 text-xs flex-1">
                      <X size={14} className="mr-1" /> Vazgeç
                    </Button>
                  </div>
                </div>
              )}

              {/* ── Action Buttons ── */}
              {!isEditing && (
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <button onClick={() => setNotesModalPetId(pet.id)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-[#6b6de3]/5 rounded-xl hover:bg-[#6b6de3]/10 transition-colors border border-[#6b6de3]/10"
                    data-testid={`open-notes-${pet.id}`}>
                    <FileText size={14} className="text-[#6b6de3] shrink-0" />
                    <span className="text-[11px] font-medium text-[#6b6de3]">Notlar</span>
                    {petNotes[pet.id]?.length > 0 && (
                      <span className="ml-auto text-[9px] bg-[#6b6de3] text-white w-4 h-4 rounded-full flex items-center justify-center font-bold">{petNotes[pet.id].length}</span>
                    )}
                  </button>
                  <button onClick={() => setLabModalPetId(pet.id)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-emerald-50 rounded-xl hover:bg-emerald-100 transition-colors border border-emerald-100"
                    data-testid={`open-lab-${pet.id}`}>
                    <FlaskConical size={14} className="text-emerald-600 shrink-0" />
                    <span className="text-[11px] font-medium text-emerald-700">Lab</span>
                    {petLabCounts[pet.id] > 0 && (
                      <span className="ml-auto text-[9px] bg-emerald-600 text-white w-4 h-4 rounded-full flex items-center justify-center font-bold">{petLabCounts[pet.id]}</span>
                    )}
                  </button>
                  <button onClick={() => setWeightModalPetId(pet.id)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-amber-50 rounded-xl hover:bg-amber-100 transition-colors border border-amber-100"
                    data-testid={`open-weight-${pet.id}`}>
                    <Weight size={14} className="text-amber-600 shrink-0" />
                    <span className="text-[11px] font-medium text-amber-700">Kilo</span>
                    {petWeightLast[pet.id] && (
                      <span className="ml-auto text-[9px] bg-amber-600 text-white px-1.5 h-4 rounded-full flex items-center justify-center font-bold">
                        {petWeightLast[pet.id].weight_grams >= 1000 ? `${(petWeightLast[pet.id].weight_grams / 1000).toFixed(1)}` : `${petWeightLast[pet.id].weight_grams}g`}
                      </span>
                    )}
                  </button>
                </div>
              )}

              {/* ── Expandable Details ── */}
              {!isEditing && (
                <div className="mt-3">
                  <button
                    onClick={() => setExpandedPets(prev => ({ ...prev, [pet.id]: !prev[pet.id] }))}
                    className="flex items-center gap-1 text-[11px] font-medium text-[#8E8E93] hover:text-gray-600 transition-colors w-full"
                    data-testid={`toggle-details-${pet.id}`}
                  >
                    <ChevronDown size={14} className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                    {isExpanded ? 'Detayları Gizle' : 'Tüm Detaylar'}
                  </button>
                  {isExpanded && (
                    <div className="mt-2 p-3 bg-[#F9F9FB] rounded-xl space-y-1.5 animate-fade-in" data-testid={`pet-details-${pet.id}`}>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                        <div className="flex items-baseline gap-1.5" data-testid={`pet-breed-${pet.id}`}>
                          <span className="text-[10px] font-medium text-[#8E8E93] uppercase">Irk</span>
                          <span className="text-xs text-gray-900">{pet.breed || <span className="text-[#C7C7CC]">-</span>}</span>
                        </div>
                        <div className="flex items-baseline gap-1.5" data-testid={`pet-gender-${pet.id}`}>
                          <span className="text-[10px] font-medium text-[#8E8E93] uppercase">Cinsiyet</span>
                          <span className="text-xs text-gray-900">{pet.gender ? getGenderName(pet.gender) : <span className="text-[#C7C7CC]">-</span>}</span>
                        </div>
                        <div className="flex items-baseline gap-1.5" data-testid={`pet-color-${pet.id}`}>
                          <span className="text-[10px] font-medium text-[#8E8E93] uppercase">Renk</span>
                          <span className="text-xs text-gray-900">{pet.color || <span className="text-[#C7C7CC]">-</span>}</span>
                        </div>
                        <div className="flex items-baseline gap-1.5" data-testid={`pet-birthdate-${pet.id}`}>
                          <span className="text-[10px] font-medium text-[#8E8E93] uppercase">Doğum</span>
                          <span className="text-xs text-gray-900">{pet.birth_date ? new Date(pet.birth_date + 'T00:00:00').toLocaleDateString('tr-TR') : <span className="text-[#C7C7CC]">-</span>}</span>
                        </div>
                        <div className="flex items-baseline gap-1.5" data-testid={`pet-weight-detail-${pet.id}`}>
                          <span className="text-[10px] font-medium text-[#8E8E93] uppercase">Ağırlık</span>
                          <span className="text-xs text-gray-900">{weightDisplay || <span className="text-[#C7C7CC]">-</span>}</span>
                        </div>
                        <div className="flex items-baseline gap-1.5" data-testid={`pet-neutered-detail-${pet.id}`}>
                          <span className="text-[10px] font-medium text-[#8E8E93] uppercase">Kısır</span>
                          {pet.is_neutered === true ? <span className="text-xs font-medium text-green-600">Evet</span>
                            : pet.is_neutered === false ? <span className="text-xs font-medium text-red-500">Hayır</span>
                            : <span className="text-xs text-[#C7C7CC]">-</span>}
                        </div>
                        <div className="flex items-baseline gap-1.5" data-testid={`pet-microchip-detail-${pet.id}`}>
                          <span className="text-[10px] font-medium text-[#8E8E93] uppercase">Mikroçip</span>
                          <span className="text-xs text-gray-900 truncate max-w-[120px]">{pet.microchip_no || <span className="text-[#C7C7CC]">-</span>}</span>
                        </div>
                        <div className="flex items-baseline gap-1.5" data-testid={`pet-registered-${pet.id}`}>
                          <span className="text-[10px] font-medium text-[#8E8E93] uppercase">Kayıt</span>
                          <span className="text-xs text-gray-900">{pet.created_at ? new Date(pet.created_at).toLocaleDateString('tr-TR') : <span className="text-[#C7C7CC]">-</span>}</span>
                        </div>
                      </div>
                      {pet.notes && (
                        <div className="pt-1.5 border-t border-gray-100 mt-1.5">
                          <span className="text-[10px] font-medium text-[#8E8E93] uppercase">Not</span>
                          <p className="text-xs text-gray-700 mt-0.5">{pet.notes}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ── Compact Reminders ── */}
              {!isEditing && upcomingReminders.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-1.5 mb-2">
                    <AlertCircle size={12} className="text-[#FF9500]" />
                    <span className="text-[10px] font-semibold text-[#8E8E93] uppercase tracking-wider">Hatırlatmalar</span>
                  </div>
                  <div className="space-y-1">
                    {upcomingReminders.map((reminder) => (
                      <div key={reminder.id} className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg ${reminder.type === 'vaccine' ? 'bg-green-50' : 'bg-blue-50'}`}>
                        <div className="flex items-center gap-1.5">
                          {reminder.type === 'vaccine' ? <Syringe size={12} className="text-green-600" /> : <Stethoscope size={12} className="text-blue-600" />}
                          <span className="text-xs font-medium text-gray-800">{reminder.name}</span>
                        </div>
                        <span className={`text-[11px] font-semibold ${reminder.type === 'vaccine' ? 'text-green-600' : 'text-blue-600'}`}>
                          {new Date(reminder.next_due_date).toLocaleDateString('tr-TR')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          );
        })}

        {/* Operations History */}
        {(filteredOperations.length > 0 || opTypeFilter) && (
          <div className="ios-card animate-fade-in">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-[#8E8E93] uppercase tracking-wider">{`İşlem Geçmişi`}</h2>
              {opTypes.length > 1 && (
                <div className="flex gap-1">
                  <button onClick={() => setOpTypeFilter('')}
                    className={`px-2 py-1 rounded-lg text-[10px] font-medium transition-colors ${!opTypeFilter ? 'bg-[#6b6de3] text-white' : 'bg-[#F2F2F7] text-gray-600'}`}
                    data-testid="op-filter-all">Tümü</button>
                  {opTypes.map(t => (
                    <button key={t} onClick={() => setOpTypeFilter(opTypeFilter === t ? '' : t)}
                      className={`px-2 py-1 rounded-lg text-[10px] font-medium transition-colors ${opTypeFilter === t ? 'bg-[#6b6de3] text-white' : 'bg-[#F2F2F7] text-gray-600'}`}
                      data-testid={`op-filter-${t}`}>
                      {t === 'vaccine' ? 'Aşı' : t === 'treatment' ? 'Tedavi' : t === 'service' ? 'Hizmet' : t === 'product' ? 'Ürün' : t}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              {filteredOperations.map((op) => (
                <div key={op.id} className="flex items-center justify-between p-3 bg-[#F2F2F7] rounded-xl" data-testid={`operation-${op.id}`}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`badge ${getOperationBadgeClass(op.type)}`}>{getOperationIcon(op.type)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate">{op.name}</p>
                      <p className="text-xs text-[#8E8E93]">{op.pet_name} {'•'} {new Date(op.date).toLocaleDateString('tr-TR')}{op.user_name && <span className="text-[#6b6de3]"> • {op.user_name}</span>}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <span className="font-semibold text-gray-900 text-sm">{formatCurrency(op.price)}</span>
                    <button onClick={() => setDeleteConfirm(op)} disabled={deletingId === op.id} className="p-2 rounded-full hover:bg-red-100 transition-colors text-[#8E8E93] hover:text-red-500" data-testid={`delete-operation-${op.id}`}>
                      <Trash2 size={16} className={deletingId === op.id ? 'animate-pulse' : ''} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {filteredOperations.length === 0 && (
          <div className="ios-card text-center py-8">
            <PawPrint size={48} className="text-[#C7C7CC] mx-auto mb-3" />
            <p className="text-[#8E8E93]">{`Henüz işlem kaydı yok`}</p>
            <Button onClick={() => setQuickOpModal({ open: true, pet: null })} className="mt-4 bg-[#6b6de3] hover:bg-[#5658c4] rounded-full">
              <Plus size={18} className="mr-1" /> {`İlk İşlemi Ekle`}
            </Button>
          </div>
        )}
      </main>

      <BottomNav />

      <QuickPetModal
        open={addPetOpen}
        onClose={(saved) => {
          setAddPetOpen(false);
          if (saved) loadData();
        }}
        preselectedCustomer={customer ? { id: customer.id, name: customer.name, phone: customer.phone } : null}
      />

      {/* Delete Confirmation Modal */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{`İşlemi Sil`}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteConfirm && (<><strong>{deleteConfirm.name}</strong>{` işlemini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`}</>)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">{`Vazgeç`}</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteConfirm && handleDeleteOperation(deleteConfirm.id)} className="bg-red-500 hover:bg-red-600 rounded-full" data-testid="confirm-delete-btn">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Balance Management Modal */}
      {showBalanceModal && (
        <div className="fixed inset-x-0 top-0 bottom-[65px] z-[9998] flex sm:inset-0 sm:items-center sm:justify-center sm:p-3" data-testid="balance-modal">
          <div className="absolute inset-0 bg-black/40" onClick={() => setBalanceModal(false)} />
          <div className="relative bg-white sm:rounded-2xl w-full sm:max-w-md lg:max-w-3xl flex flex-col h-full sm:h-auto sm:max-h-[75vh] overflow-y-auto shadow-2xl">
            <div className="p-6 space-y-5">
              {/* Header */}
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900">Bakiye Yönetimi</h2>
                <button onClick={() => { setShowBalanceModal(false); loadData(); }} className="p-2 rounded-full hover:bg-gray-100" data-testid="close-balance-modal">
                  <X size={20} />
                </button>
              </div>

              {/* Current Balance */}
              <div className={`text-center py-4 rounded-2xl ${balanceData.balance < 0 ? 'bg-red-50' : balanceData.balance > 0 ? 'bg-green-50' : 'bg-[#F2F2F7]'}`}>
                <p className="text-xs text-[#8E8E93] uppercase tracking-wider mb-1">Mevcut Bakiye</p>
                <p className={`text-3xl font-bold ${balanceData.balance < 0 ? 'text-[#FF3B30]' : balanceData.balance > 0 ? 'text-[#34C759]' : 'text-gray-900'}`} data-testid="current-balance">
                  {balanceData.balance < 0 ? `-${formatCurrency(Math.abs(balanceData.balance))}` : balanceData.balance > 0 ? `+${formatCurrency(balanceData.balance)}` : '0 TL'}
                </p>
                <p className="text-xs text-[#8E8E93] mt-1">
                  {balanceData.balance < 0 ? 'Müşteri Borçlu' : balanceData.balance > 0 ? 'Müşteri Alacaklı' : ''}
                </p>
              </div>

              {/* SMS / WhatsApp - only when in debt */}
              {Number(balanceData.balance) < 0 && customer?.phone && (
                <div className="flex items-center justify-center gap-2" data-testid="customer-balance-notification-actions">
                  <button
                    onClick={handleBalanceSms}
                    disabled={balanceSmsSending}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm font-medium transition-colors disabled:opacity-50 border border-blue-200"
                    data-testid="customer-balance-sms-btn"
                  >
                    <MessageCircle size={15} />
                    SMS Gönder
                  </button>
                  <button
                    onClick={handleBalanceWhatsApp}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-green-50 hover:bg-green-100 text-green-700 text-sm font-medium transition-colors border border-green-200"
                    data-testid="customer-balance-whatsapp-btn"
                  >
                    <Send size={15} />
                    WhatsApp
                  </button>
                </div>
              )}

              {/* Quick Add */}
              <div className="bg-[#F2F2F7] rounded-2xl p-4 space-y-3">
                <div className="relative">
                  <Input type="number" value={newTx.amount} onChange={(e) => setNewTx(p => ({ ...p, amount: e.target.value }))}
                    placeholder="Tutar" className="h-12 bg-white border-0 rounded-xl pr-8 text-base" data-testid="tx-amount" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8E8E93]">TL</span>
                </div>
                <Input value={newTx.description} onChange={(e) => setNewTx(p => ({ ...p, description: e.target.value }))}
                  placeholder="Açıklama (opsiyonel)" className="h-10 bg-white border-0 rounded-xl text-sm" data-testid="tx-description" />
                <div className="grid grid-cols-2 gap-3">
                  <Button onClick={() => { newTx.type = 'tahsilat'; handleAddTx(); }}
                    disabled={!newTx.amount}
                    className="h-11 rounded-xl bg-[#34C759] hover:bg-green-600 text-sm font-semibold disabled:opacity-40" data-testid="add-tahsilat-btn">
                    <Banknote size={16} className="mr-1.5" /> Tahsilat
                  </Button>
                  <Button onClick={() => { newTx.type = 'borc'; handleAddTx(); }}
                    disabled={!newTx.amount}
                    className="h-11 rounded-xl bg-[#FF3B30] hover:bg-red-600 text-sm font-semibold disabled:opacity-40" data-testid="add-borc-btn">
                    <Clock size={16} className="mr-1.5" /> Borç
                  </Button>
                </div>
              </div>

              {/* Transaction History */}
              <div>
                <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider mb-3">İşlem Geçmişi</p>
                {balanceLoading ? (
                  <div className="py-6 flex justify-center"><Loader size="sm" /></div>
                ) : balanceData.transactions.length > 0 ? (
                  <div className="space-y-2">
                    {balanceData.transactions.map((tx) => (
                      <div key={tx.id} className="bg-[#F2F2F7] rounded-xl p-3" data-testid={`tx-${tx.id}`}>
                        {editingTxId === tx.id ? (
                          <div className="space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                              <button onClick={() => setEditTxForm(p => ({ ...p, type: 'tahsilat' }))}
                                className={`py-2 rounded-lg text-xs font-medium ${editTxForm.type === 'tahsilat' ? 'bg-[#34C759] text-white' : 'bg-white text-gray-700'}`}>
                                Tahsilat
                              </button>
                              <button onClick={() => setEditTxForm(p => ({ ...p, type: 'borc' }))}
                                className={`py-2 rounded-lg text-xs font-medium ${editTxForm.type === 'borc' ? 'bg-[#FF3B30] text-white' : 'bg-white text-gray-700'}`}>
                                Borç
                              </button>
                            </div>
                            <div className="flex gap-2">
                              <Input type="number" value={editTxForm.amount} onChange={(e) => setEditTxForm(p => ({ ...p, amount: e.target.value }))}
                                className="h-9 bg-white border-0 rounded-lg text-sm flex-1" />
                              <Input value={editTxForm.description} onChange={(e) => setEditTxForm(p => ({ ...p, description: e.target.value }))}
                                placeholder="Açıklama" className="h-9 bg-white border-0 rounded-lg text-sm flex-1" />
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => handleUpdateTx(tx.id)} className="bg-[#34C759] hover:bg-green-600 rounded-lg h-8 px-3 text-xs flex-1">
                                <Check size={14} className="mr-1" /> Kaydet
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setEditingTxId(null)} className="rounded-lg h-8 px-3 text-xs">
                                İptal
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tx.type === 'tahsilat' ? 'bg-green-100 text-[#34C759]' : 'bg-red-100 text-[#FF3B30]'}`}>
                                  {tx.type === 'tahsilat' ? '+' : '-'}{formatCurrency(tx.amount)}
                                </span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${tx.type === 'tahsilat' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                  {tx.type === 'tahsilat' ? 'Tahsilat' : 'Borç'}
                                </span>
                              </div>
                              {tx.description && <p className="text-xs text-[#8E8E93] mt-1 truncate">{tx.description}</p>}
                              <p className="text-[10px] text-[#C7C7CC] mt-0.5">{new Date(tx.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                            </div>
                            <div className="flex items-center gap-1 ml-2">
                              <button onClick={() => { setEditingTxId(tx.id); setEditTxForm({ type: tx.type, amount: String(tx.amount), description: tx.description || '' }); }}
                                className="p-1.5 rounded-full hover:bg-white" data-testid={`edit-tx-${tx.id}`}>
                                <Pencil size={14} className="text-[#8E8E93]" />
                              </button>
                              {deletingTxId === tx.id ? (
                                <div className="flex items-center gap-1">
                                  <button onClick={() => handleDeleteTx(tx.id)} className="p-1.5 rounded-full bg-red-100 hover:bg-red-200" data-testid={`confirm-delete-tx-${tx.id}`}>
                                    <Check size={14} className="text-[#FF3B30]" />
                                  </button>
                                  <button onClick={() => setDeletingTxId(null)} className="p-1.5 rounded-full hover:bg-white">
                                    <X size={14} className="text-[#8E8E93]" />
                                  </button>
                                </div>
                              ) : (
                                <button onClick={() => setDeletingTxId(tx.id)} className="p-1.5 rounded-full hover:bg-white" data-testid={`delete-tx-${tx.id}`}>
                                  <Trash2 size={14} className="text-[#FF3B30]" />
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 bg-[#F2F2F7] rounded-xl">
                    <Wallet size={32} className="text-[#C7C7CC] mx-auto mb-2" />
                    <p className="text-sm text-[#8E8E93]">Henüz işlem yok</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Operation Modal */}
      {customer && (
        <QuickOperationModal
          open={quickOpModal.open}
          onClose={(success) => {
            setQuickOpModal({ open: false, pet: null });
            if (success) loadData();
          }}
          initialCustomer={customer}
          initialPet={quickOpModal.pet}
        />
      )}

      {/* SMS Preferences Modal */}
      {customer && (
        <CustomerSmsModal
          open={smsModalOpen}
          onClose={() => setSmsModalOpen(false)}
          customer={customer}
        />
      )}

      {/* Pet Notes Modal */}
      {notesModalPetId && (() => {
        const modalPet = customer?.pets?.find(p => p.id === notesModalPetId);
        return <PetNotesModal petId={notesModalPetId} petName={modalPet?.name || ''} onClose={() => { setNotesModalPetId(null); loadAllCounts(customer.pets); }} />;
      })()}

      {/* Pet Lab Modal */}
      {labModalPetId && (() => {
        const modalPet = customer?.pets?.find(p => p.id === labModalPetId);
        return <PetLabModal petId={labModalPetId} petName={modalPet?.name || ''} onClose={() => { setLabModalPetId(null); loadAllCounts(customer.pets); }} />;
      })()}

      {/* Pet Weight Modal */}
      {weightModalPetId && (() => {
        const modalPet = customer?.pets?.find(p => p.id === weightModalPetId);
        return <PetWeightModal petId={weightModalPetId} petName={modalPet?.name || ''} onClose={() => { setWeightModalPetId(null); loadAllCounts(customer.pets); }} />;
      })()}
    </div>
  );
};

export default CustomerDetail;

function BreedSelect({ breeds, value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handle = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const filtered = search
    ? breeds.filter(b => b.toLowerCase().includes(search.toLowerCase()))
    : breeds;

  return (
    <div className="relative mt-1" ref={ref}>
      <button type="button" onClick={() => { setOpen(!open); setSearch(''); }}
        className={`w-full h-10 px-3 rounded-lg text-sm text-left flex items-center justify-between transition-colors ${
          value ? 'bg-white text-gray-900' : 'bg-white text-[#8E8E93]'
        }`} data-testid={testId}>
        <span className="truncate">{value || 'Irk seçin'}</span>
        <ChevronDown size={14} className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white rounded-xl shadow-lg border border-gray-200 max-h-[220px] flex flex-col overflow-hidden">
          <div className="p-1.5 border-b border-gray-100">
            <input type="text" autoFocus placeholder="Irk ara..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="w-full h-8 px-2.5 text-sm rounded-lg bg-[#F2F2F7] border-0 outline-none"
              data-testid={`${testId}-search`} />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <p className="text-xs text-[#8E8E93] text-center py-3">Sonuç bulunamadı</p>
            ) : filtered.map(b => (
              <button key={b} type="button"
                onClick={() => { onChange(b); setOpen(false); setSearch(''); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-[#F2F2F7] transition-colors ${
                  value === b ? 'bg-[#6b6de3]/10 text-[#6b6de3] font-medium' : 'text-gray-800'
                }`}>{b}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
